import StepButtons from './StepButtons'
import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { Label, Input, Row, Col, Card, CardBody, CardTitle, Button } from 'reactstrap';
import Select from 'react-select';
import { ITeamsRequest, TeamsRequest, TeamsClient, CreateTeamsCommand } from '../../../WorldDoomLeague';
import { ErrorContext } from '../../../state';
import { StepWizardChildProps } from 'react-step-wizard';
import { ICaptainList, IPlayerList } from './NewSeasonWizardInterfaces';

interface AddTeamsProps extends StepWizardChildProps {
    players: IPlayerList[],
    setPlayers: React.Dispatch<React.SetStateAction<IPlayerList[]>>,
    setCaptains: React.Dispatch<React.SetStateAction<ICaptainList[]>>,
    season: number
};

const AddTeams = (props: AddTeamsProps) => {
    const error = useContext(ErrorContext);
    const [teamList, setTeamList] = useState<ITeamsRequest[]>([{
        teamName: null,
        teamAbbreviation: null,
        teamCaptain: null
    }]);
    const [amountTeams, setAmountTeams] = useState(6);
    const [createdTeams, setCreatedTeams] = useState(0);
    const [toggle, setToggle] = useState(false);
    const [canSubmitTeams, setCanSubmitTeams] = useState(false);

    useEffect(() => {
        const pad_array = (arr, len, fill) => {
            return arr.concat(Array(len).fill(fill)).slice(0, len);
        };

        const newTeamList = pad_array(teamList, amountTeams, {
            teamName: null,
            teamAbbreviation: null,
            teamCaptain: null
        });
        setTeamList(newTeamList);
    }, [amountTeams]);

    const enableTeamList = () => {
        setToggle(true);
    };

    const handleTeamNameChange = (idx: number, name: string) => {
        const newTeamName = teamList.map((team, sidx) => {
            if (idx !== sidx) return team;
            return { ...team, teamName: name };
        });

        setTeamList(newTeamName);
        checkIfFormComplete(newTeamName);
    };

    const handleTeamAbvChange = (idx: number, abv: string) => {
        const newTeamName = teamList.map((team, sidx) => {
            if (idx !== sidx) return team;
            return { ...team, teamAbbreviation: abv };
        });

        setTeamList(newTeamName);
        checkIfFormComplete(newTeamName);
    };

    const handlePlayerSelected = (idx: number, playerId: number) => {
        const newPlayerArray = props.players.map((s, _idx) => {
            if (s.id !== playerId) return s;
            return { ...s, isdisabled: true };
        })

        if (teamList[idx].teamCaptain !== null) {
            const reenabledOldPlayer = newPlayerArray.map((s, _idx) => {
                if (s.id !== teamList[idx].teamCaptain) return s;
                return { ...s, isdisabled: false };
            });
            props.setPlayers(reenabledOldPlayer);
        } else {
            props.setPlayers(newPlayerArray);
        }

        const newCaptain = teamList.map((team, sidx) => {
            if (idx !== sidx) return team;
            return { ...team, teamCaptain: playerId };
        });

        setTeamList(newCaptain);
        checkIfFormComplete(newCaptain);
    };

    const createTeams = async (evt) => {
        try {
            let client = new TeamsClient();
            const command = new CreateTeamsCommand;
            // Need to do this to get around an NSwag generator bug.
            // https://github.com/RicoSuter/NSwag/issues/2862
            let teams: TeamsRequest[] = [];
            for (var idx = 0; idx < teamList.length; idx++) {
                var addTeam = new TeamsRequest();
                addTeam.teamName = teamList[idx].teamName;
                addTeam.teamAbbreviation = teamList[idx].teamAbbreviation;
                addTeam.teamCaptain = teamList[idx].teamCaptain;
                teams.push(addTeam);
            }

            command.season = props.season;
            command.teamsRequestList = teams;
            const response = await client.createTeams(command);
            setCreatedTeams(response);
            setCanSubmitTeams(false);
            setAmountTeams(response);
            const players = createPlayersList();
            props.setPlayers(players);
            const captains = createCaptainsList();
            props.setCaptains(captains);
        } catch (e) {
            error.setError(JSON.parse(e.response));
        }
    };

    const createCaptainsList = () => {
        const captainList: ICaptainList[] = teamList.map((s, _idx) => {
            var captain = props.players.find(i => i.id === s.teamCaptain) as ICaptainList;
            captain.playersLeft = 0;
            captain.isdisabled = false;
            return captain;
        });
        return captainList;
    };

    const createPlayersList = () => {
        return props.players.filter((s, _idx) => s.isdisabled !== true);
    };

    const checkIfFormComplete = (teams) => {
        if (teams.every(element => element.teamAbbreviation && element.teamCaptain && element.teamName &&
            element.teamAbbreviation.length > 0 && element.teamName.length > 0)) {
            setCanSubmitTeams(true);
        } else {
            setCanSubmitTeams(false);
        }
    };

    // create a list for each engine.
    const renderPlayerDropdown = (idx) => {
        let select = null;
        if (props.players.length > 0) {
            select = (
                <Select
                    options={props.players}
                    onChange={e => handlePlayerSelected(idx, e.id)}
                    value={props.players.find(o => o.id == teamList[idx].teamCaptain) || null}
                    getOptionValue={value => value.id.toString()}
                    getOptionLabel={label => label.playerName}
                    isOptionDisabled={(option) => option.isdisabled}
                    isDisabled={(createdTeams === amountTeams)}
                    isSearchable={true}
                    theme={theme => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                            ...theme.colors,
                            primary25: 'dimgrey',
                            neutral0: 'black',
                            neutral80: 'white'
                        },
                    })}
                />)
        } else {
            select = (<Select options={[{ label: "No players left!", value: "Not" }]} isDisabled={(createdTeams === amountTeams)} theme={theme => ({
                ...theme,
                borderRadius: 0,
                colors: {
                    ...theme.colors,
                    primary25: 'dimgrey',
                    neutral0: 'black',
                    neutral80: 'white'
                },
            })} />);
        }

        return (select);
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm="12" md={{ size: 6, offset: 3 }}>
                    <h3 className='text-center'>Add Teams</h3>
                    <p>This step will add the teams that will play during the season.</p>
                    <Label for="amountTeams">Amount of teams</Label>
                    <Input placeholder="Amount" name="amountTeams" min={4} max={512} type="number" step="2" value={amountTeams} disabled={toggle} onChange={e => setAmountTeams(parseInt(e.target.value, 10))} />
                    <br />
                    <Button color="primary" size="lg" block disabled={toggle} onClick={enableTeamList}>Generate Team Cards</Button>
                    <hr />
                </Col>
            </Row>
            <Row>
                {teamList && toggle && (teamList.map((team, index) => (
                    <Col xs="6" sm="4">
                        <Card>
                            <CardBody>
                                <CardTitle tag="h5">Team</CardTitle>
                                <Label for="teamName">Team Name</Label>
                                <Input type="text" value={team.teamName} placeholder="Super Chargers" maxLength={64} disabled={(createdTeams === amountTeams)} onChange={e => handleTeamNameChange(index, e.target.value)} />
                                <Label for="teamName">Team Abbreviation</Label>
                                <Input type="text" value={team.teamAbbreviation} placeholder="SUC" maxLength={5} disabled={(createdTeams === amountTeams)} onChange={e => handleTeamAbvChange(index, e.target.value)} />
                                <Label for="teamName">Team Captain</Label>
                                {renderPlayerDropdown(index)}
                            </CardBody>
                        </Card>
                        <br />
                    </Col>
                    )))}
            </Row>
            <Row>
                <Col sm="12" md={{ size: 6, offset: 3 }}>
                    <Button color="primary" size="lg" block disabled={!canSubmitTeams} onClick={createTeams}>Create Teams</Button>
                </Col>
            </Row>
        <Row>
                <Col sm="12" md={{ size: 6, offset: 3 }}>
                    <StepButtons {...props} step={3} disabled={!(createdTeams === amountTeams)} />
            </Col>
        </Row>
        </React.Fragment>
    );
};

export default AddTeams;